import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

const supported =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

const isIOS = typeof navigator !== "undefined" && /iPhone|iPad|iPod/.test(navigator.userAgent);
const isStandalone = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);

export const EnablePushButton = ({ variant = "default", showTest = false, className = "" }) => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!supported) { setChecking(false); return; }
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setEnabled(!!sub && Notification.permission === "granted");
      } catch { /* silent */ }
      finally { setChecking(false); }
    })();
  }, []);

  if (!supported) {
    // iOS Safari before install-to-home-screen exposes no PushManager. Show a hint.
    if (isIOS && !isStandalone) {
      return (
        <div className="text-xs text-muted-foreground max-w-[260px] text-right" data-testid="ios-push-hint">
          Install to Home Screen first (Share → Add to Home Screen) to enable notifications on iOS.
        </div>
      );
    }
    return null;
  }

  const enable = async () => {
    setLoading(true);
    try {
      if (Notification.permission === "denied") {
        toast.error("Notifications are blocked. Enable them in your browser settings.");
        return;
      }
      if (Notification.permission !== "granted") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") {
          toast.error("Permission denied");
          return;
        }
      }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-key");
      if (!data?.publicKey) { toast.error("Server key unavailable"); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
      await api.post("/push/subscribe", sub.toJSON());
      setEnabled(true);
      toast.success("Notifications enabled");
    } catch (e) {
      console.error("push enable failed:", e);
      toast.error(e?.message || "Failed to enable notifications");
    } finally { setLoading(false); }
  };

  const disable = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete("/push/subscribe", { data: sub.toJSON() }).catch(() => null);
        await sub.unsubscribe();
      }
      setEnabled(false);
      toast.success("Notifications disabled");
    } catch { toast.error("Failed to disable"); }
    finally { setLoading(false); }
  };

  const test = async () => {
    try {
      await api.post("/push/test");
      toast.success("Test push sent");
    } catch { toast.error("Failed to send test"); }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Button
        onClick={enabled ? disable : enable}
        disabled={loading || checking}
        variant={variant === "outline" ? "outline" : undefined}
        className={variant === "outline" ? "border-border h-10" : "brand-bg text-black hover:opacity-90 h-10"}
        data-testid="enable-push-btn"
      >
        {loading || checking ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> :
         enabled ? <BellOff className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> :
         <Bell className="h-4 w-4 mr-1.5" strokeWidth={1.5} />}
        {enabled ? "Disable notifications" : "Enable notifications"}
      </Button>
      {enabled && showTest && (
        <Button onClick={test} variant="outline" className="border-border h-10" data-testid="test-push-btn">
          Send test
        </Button>
      )}
    </div>
  );
};
